import { createContext, memo, useContext, useMemo, useState, useEffect, useCallback } from 'react';

export type StaticAccordionId =
  | 'patientPayerDetails'
  | 'maximums'
  | 'deductibles'
  | 'provisions'
  | 'history'
  | 'appointmentProceduresTable'
  | 'favoriteCDTCodeTable';

export type AccordionId = StaticAccordionId | string;

type AccordionContextState = {
  activeAccordionIds: Set<AccordionId>;
  accordionIds: AccordionId[];
  toggleAccordion: (accordionId: AccordionId) => void;
};

type AccordionProviderProps = {
  children: React.ReactNode;
  accordionIds: AccordionId[];
};

export const AccordionContext = createContext<AccordionContextState>({
  activeAccordionIds: new Set<AccordionId>(),
  toggleAccordion: () => {},
  accordionIds: [],
});

export const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('useAccordionContext must be used within an AccordionProvider');
  }
  return context;
};

export const AccordionProvider = memo(({ children, accordionIds }: AccordionProviderProps) => {
  const [activeAccordionIds, setActiveAccordionIds] = useState<Set<AccordionId>>(
    () => new Set([...accordionIds] as AccordionId[])
  );

  useEffect(() => {
    if (accordionIds.length > 0) {
      setActiveAccordionIds(new Set([...accordionIds] as AccordionId[]));
    }
  }, [accordionIds]);

  const toggleAccordion = useCallback((accordionId: AccordionId) => {
    setActiveAccordionIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(accordionId)) {
        newIds.delete(accordionId);
      } else {
        newIds.add(accordionId);
      }
      return newIds;
    });
  }, []);

  const providerValue = useMemo(() => {
    return {
      activeAccordionIds,
      accordionIds,
      toggleAccordion,
    };
  }, [activeAccordionIds, toggleAccordion, accordionIds]);

  return <AccordionContext.Provider value={providerValue}>{children}</AccordionContext.Provider>;
});

AccordionProvider.displayName = 'AccordionProvider';
